import { useEffect, useState } from 'react'
import _ from 'lodash'
import {
  Table,
  Separator,
  Input,
  Button,
  Tooltip,
} from '@design-systems/canvas-ui'
import { Icon, InfoIcon } from '@design-systems/react-icons'

const ReactionGrid = (props) => {
  const [valueMatrix, setValueMatrix] = useState(props.value ? props.value : [])

  useEffect(() => {
    if (!_.isEqual(valueMatrix, props.value) && !props.isreadOnly) {
      props.updateGridValues(
        props.groupName,
        props.heading,
        props.section,
        valueMatrix,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMatrix])

  /**
   * Build editable Canvas v8 Table (columnDefs + rowData) for the reaction grid.
   */
  const buildEditableTable = () => {
    const columnDefs = [
      {
        id: 'label',
        header: '',
        isRowHeader: true,
        cell: ({ row }) => row.original.__field,
      },
      ...props.options.map((eachOption, indexOption) => ({
        id: `option-${indexOption}`,
        header: eachOption,
        cell: ({ row }) => {
          const eachField = row.original.__field
          const indexField = row.index
          const selectedValue = valueMatrix?.filter(
            (eachRow) => eachRow.key === eachField,
          )[0]?.value
          const inputId = `${props.id}-${indexField}-${indexOption}`
          const groupName = `${props.id}-${indexField}`
          return (
            <input
              type="radio"
              id={inputId}
              data-testid={inputId}
              name={groupName}
              value={eachOption}
              checked={selectedValue === eachOption}
              disabled={props.isreadOnly}
              onChange={() => updateGridMatrix(eachOption, eachField)}
            />
          )
        },
      })),
    ]

    const rowData = props.fields.map((eachField, indexField) => ({
      id: `${props.id}-row-${indexField}`,
      __field: eachField,
    }))

    return (
      <Table
        name={`Reaction grid for ${props.heading}`}
        data-testid={`table-${props.id}`}
        cellPadding="dense"
        hasGrid
        data={{ columnDefs, rowData }}
      />
    )
  }

  /**
   * Update the reaction grid matrix value
   * @param {*} id
   * @param {*} value
   * @param {*} eachField
   */
  const updateGridMatrix = (value, eachField) => {
    let newNode = {
      key: eachField,
      value: value,
    }

    let foundRow = valueMatrix.filter((eachRow) => eachRow.key === eachField)[0]
    if (foundRow) {
      let newData = valueMatrix.map((eachRow, rowIndex) => {
        return eachRow.key !== eachField ? eachRow : newNode
      })
      setValueMatrix([...newData])
    } else {
      setValueMatrix([...valueMatrix, newNode])
    }
  }

  const removeReactionGrid = () => {
    props.removeGrid(props.heading, props.groupName, props.section)
  }

  const renderCapabilityRowsReadonly = () => {
    const columnDefs = [
      {
        id: 'key',
        header: '',
        isRowHeader: true,
        cell: ({ row }) => (
          <Input.Label className="text-grey rfi-static-data-style">
            {row.original.key}
          </Input.Label>
        ),
      },
      {
        id: 'value',
        header: '',
        cell: ({ row }) => (
          <Input.Label className="text-grey rfi-static-data-style">
            {row.original.value}
          </Input.Label>
        ),
      },
    ]

    const rowData = (valueMatrix || []).map((eachValue, index) => ({
      id: `${props.id}-ro-${index}-${eachValue.key}`,
      key: eachValue.key,
      value: eachValue.value,
    }))

    return (
      <Table
        name={`Reaction grid readonly for ${props.heading}`}
        data-testid={`table-readonly-${props.id}`}
        cellPadding="dense"
        hasGrid
        data={{ columnDefs, rowData }}
      />
    )
  }

  if (props.heading) {
    return (
      <>
        <div className="row-centre space-between hc8-mt-2x hc8-mb-2x">
          <div className="align-centre-left">
            <Input.Label className="align-centre-centre hc8-fs-150 hc8-mb-1x">
              <p
                className={['align-centre-centre', 'hc8-fs-150']
                  .join(' ')
                  .concat(props.allowRemove ? '' : ' font-weight-500')}
              >
                {props.heading}
              </p>
            </Input.Label>
            {props.info && (
              <Tooltip
                className="hc8-ml-2x"
                content={props.info}
                location="right"
              >
                <Icon className="row-centre" svg={InfoIcon} aria-hidden />
              </Tooltip>
            )}
          </div>

          {props.allowRemove && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => removeReactionGrid()}
            >
              Remove
            </Button>
          )}
        </div>

        {!props.isreadOnly ? (
          <>
            {buildEditableTable()}
            <Separator />
          </>
        ) : (
          <>{renderCapabilityRowsReadonly()}</>
        )}
      </>
    )
  } else {
    return null
  }
}

export default ReactionGrid
